<script setup>
  import { computed, ref } from 'vue';

  const currentIndex = ref();
  const graph = ref(null);
  const currentDatum = ref(null);

  const props = defineProps({
    series: {
      type: Array,
      default: () => []
    },
    data: {
      type: Array,
      default: () => []
    }
  });

  const maxCount = computed(() => {
    if (props.data.length === 0) return 1;
    return Math.max(
      ...props.data.map(d => d.total_count)
    );
  });

  function getBarHeight(count) {
    return (count / maxCount.value) * 100;
  }

  function getBarPosition(datum, serie) {
    const serie_position = props.series.indexOf(serie);
    let position = 0;

    for (let index = 0; index <= serie_position; index++) {
      const current_serie = props.series[index];
      position += getBarHeight(datum[current_serie]);
    }

    return position;
  }

  function getRectClass(serie) {
    return `color_${getSerieClass(serie)}_rect`;
  }

  function getTextClass(serie) {
    return `color_${getSerieClass(serie)}_text`;
  }

  function getSerieClass(serie) {
    const serie_position = props.series.indexOf(serie);
    return serie_position % 6 + 1;
  }

  const legendItemWidth = computed(() => {
    return 100 / (4 + props.series.length);
  });

  function labelize(serie) {
    const abbregated = serie.replace('_count', '');
    return abbregated.charAt(0).toUpperCase() + abbregated.slice(1);
  }

  const averagePath = computed(() => {
    if (props.data.length === 0) return '';

    let path = "M";
    props.data.forEach((datum, index) => {
      path += ` ${index + 0.5},${100 - getBarHeight(datum.average)}`
    });

    return path;
  });

  const medianPath = computed(() => {
    if (props.data.length === 0) return '';

    let path = "M";
    props.data.forEach((datum, index) => {
      path += ` ${index + 0.5},${100 - getBarHeight(datum.median)}`
    });

    return path;
  });

  const updateIndex = (event) => {
    if (props.data.length === 0) return 0;

    let position = event.offsetX;
    if (position < 0) {
      position = 0;
    }
    currentIndex.value = Math.floor(position / graph.value.offsetWidth * props.data.length);
    currentDatum.value = props.data[currentIndex.value]
  }

  function numberWithCommas(x) {
    if (x === undefined) {
      return '';
    }
    return x.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ",");
  }
</script>

<template>
  <div class="time-series-chart" ref="graph">
    <div v-if="data.length === 0" class="text-center text-muted py-4">
      No data
    </div>

    <div v-else>
      <svg
        :viewBox="`0 0 ${data.length} 100`"
        preserveAspectRatio="none"
        version="1.1"
        id="svg1"
        xmlns="http://www.w3.org/2000/svg"
        xmlns:svg="http://www.w3.org/2000/svg"
        @mousemove="updateIndex">
        <g v-for="(datum, index) in data"
          :id="`counts-${datum.record_date}`">
          <rect
            v-for="(serie, serie_index) in series"
            :class="getRectClass(serie)"
            :id="`rect_${serie}_${index}`"
            width="1"
            :height="getBarHeight(datum[serie])"
            :x="index"
            :y="100 - getBarPosition(datum, serie)">
          </rect>
          <rect
            class="highlight"
            style="fill:grey;"
            :id="`rect_rest_${index}`"
            width="1"
            :height="100"
            :x="index"
            :y="0">
          </rect>
        </g>
        <g
          id="average">
          <path
            style="fill:none;stroke-width:2;stroke-opacity:1"
            class="average_line"
            vector-effect="non-scaling-stroke"
            :d="averagePath"
            id="average_line" />
        </g>
        <g
          id="median">
          <path
            style="fill:none;stroke-width:2;stroke-opacity:1"
            class="median_line"
            vector-effect="non-scaling-stroke"
            :d="medianPath"
            id="median_line" />
        </g>
      </svg>
      <div class="legend mt-1">
        <span :style="`width:${legendItemWidth}%`" class="legend-col date">Date: {{ currentDatum?.record_date }}</span>
        <span :style="`width:${legendItemWidth}%`" class="legend-col total">Total: {{ numberWithCommas(currentDatum?.total_count) }}</span>
        <span :style="`width:${legendItemWidth}%`" class="legend-col average_text">Average: {{ numberWithCommas(currentDatum?.average) }}</span>
        <span :style="`width:${legendItemWidth}%`" class="legend-col median_text">Median: {{ numberWithCommas(currentDatum?.median) }}</span>
        <span :style="`width:${legendItemWidth}%`" v-for="serie in series" :class="`legend-col ${getTextClass(serie)}`">{{ labelize(serie) }}: {{ numberWithCommas(currentDatum?.[serie]) }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
  svg {
    width: 100%;
    height: 200px;
  }
  svg rect.highlight {
    fill-opacity: 0;
  }
  svg rect.highlight:hover {
    fill-opacity: 0.2;
  }
  .legend .legend-col {
    display: inline-block;
    min-width: 12.5%;
  }
  .color_1_text {
    color: #dc3545;
  }
  .color_1_rect {
    fill: #dc3545;
  }
  .color_2_text {
    color: #6c757d;
  }
  .color_2_rect {
    fill: #6c757d;
  }
  .color_3_text {
    color: #0d6efd;
  }
  .color_3_rect {
    fill: #0d6efd;
  }
  .color_4_text {
    color: #ffc008;
  }
  .color_4_rect {
    fill: #ffc008;
  }
  .color_5_text {
    color: #1a8754;
  }
  .color_5_rect {
    fill: #1a8754;
  }
  .color_6_text {
    color: #11caf0;
  }
  .color_6_rect {
    fill: #11caf0;
  }
  .average_text {
    color: #ff9300;
  }
  .average_line {
    stroke: #ff9300;
  }
  .median_text {
    color: #942192;
  }
  .median_line {
    stroke: #942192;
  }
</style>
